import { Component, inject, effect, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DataStore } from './models/data-store.model';
import { LoadingSpinnerComponent } from '../../../../shared/components/loading-spinner/loading-spinner.component';
import { AddDataLakeDialogComponent } from '../../dialogs/add-data-lake-dialog/add-data-lake-dialog.component';
import { AddLandingZoneDialogComponent } from '../../dialogs/add-landing-zone-dialog/add-landing-zone-dialog.component';
import { DataStoreProvisionStatusType } from './models/data-store-provision-status';
import { DataStoreService } from './services/data-store.service';
import { MatSort, MatSortable, MatSortModule } from '@angular/material/sort';
import { DatePipe } from '@angular/common';

@Component({
  selector: 'app-data-lakes-tab',
  imports: [
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatSortModule,
    LoadingSpinnerComponent,
    DatePipe,
  ],
  templateUrl: './data-lakes-tab.component.html',
  styleUrl: './data-lakes-tab.component.css',
})
export class DataLakesTabComponent {
  dataStoreService = inject(DataStoreService);

  columns: string[] = ['storeName', 'creationDate', 'provisionStatusTypeId'];

  dataLakesDataSource = new MatTableDataSource<DataStore>();
  landingZonesDataSource = new MatTableDataSource<DataStore>();

  provisionStatusEnum = DataStoreProvisionStatusType;

  private dataLakeSort!: MatSort;
  @ViewChild('dataLakeSort') set dataLakeMatSort(sort: MatSort) {
    if (sort) {
      this.dataLakeSort = sort;
      this.dataLakesDataSource.sort = this.dataLakeSort;

      // set default sort on load
      if (!this.dataLakeSort.active && this.dataLakesDataSource.data.length) {
        Promise.resolve().then(() => {
          this.dataLakeSort.sort({ id: 'creationDate', start: 'desc' } as MatSortable);
        });
      }
    }
  }

  private landingZoneSort!: MatSort;
  @ViewChild('landingZoneSort') set landingZoneMatSort(sort: MatSort) {
    if (sort) {
      this.landingZoneSort = sort;
      this.landingZonesDataSource.sort = this.landingZoneSort;

      // set default sort on load
      if (!this.landingZoneSort.active && this.landingZonesDataSource.data.length) {
        Promise.resolve().then(() => {
          this.landingZoneSort.sort({ id: 'creationDate', start: 'desc' } as MatSortable);
        });
      }
    }
  }

  constructor(private dialog: MatDialog) {
    this.dataLakesDataSource = new MatTableDataSource<DataStore>([]);
    this.landingZonesDataSource = new MatTableDataSource<DataStore>([]);

    effect(() => {
      const datalakes = this.dataStoreService.dataLakes() as DataStore[] ?? [];
      this.dataLakesDataSource.data = datalakes;
    });

    effect(() => {
      const landingZones = this.dataStoreService.landingZones() as DataStore[] ?? [];
      this.landingZonesDataSource.data = landingZones;
    });
  }

  ngAfterViewInit() {
    this.dataLakesDataSource.sort = this.dataLakeSort;
    this.landingZonesDataSource.sort = this.landingZoneSort;

    // provide custom sorting accessor for complex properties
    this.dataLakesDataSource.sortingDataAccessor = (item, property) => {
      switch (property) {
        case 'creationDate': 
          return item.createDateTimeUtc ? new Date(item.createDateTimeUtc).getTime() : 0;
        default: 
          const value = (item as any)[property];
          return typeof value === 'string' ? value.toLowerCase(): value ?? '';
      }
    };

    this.landingZonesDataSource.sortingDataAccessor = (item, property) => {
      switch (property) {
        case 'creationDate': 
          return item.createDateTimeUtc ? new Date(item.createDateTimeUtc).getTime() : 0;
        default: 
          const value = (item as any)[property];
          return typeof value === 'string' ? value.toLowerCase(): value ?? '';
      }
    };
  }

  openAddDataLakeDialog(): void {
    const dialogRef = this.dialog.open(AddDataLakeDialogComponent, {
      width: '560px',
      disableClose: true,
    });

    dialogRef.beforeClosed().subscribe((dataLakes: DataStore[]) => {
      if (dataLakes) {
        this.dataLakesDataSource.data = this.dataLakesDataSource.data.concat(dataLakes);
        this.dataStoreService.dataLakes.set(this.dataLakesDataSource.data);
      }
    });
  }

  openAddLandingZoneDialog(): void {
    const dialogRef = this.dialog.open(AddLandingZoneDialogComponent, {
      width: '560px',
      disableClose: true,
    });

    dialogRef.beforeClosed().subscribe((landingZones: DataStore[]) => {
      if (landingZones) {
        this.landingZonesDataSource.data = this.landingZonesDataSource.data.concat(landingZones);
        this.dataStoreService.landingZones.set(this.landingZonesDataSource.data);
      }
    });
  }
}
